// GENERATED from messaging-platform/packages/chat-core/src/hub-connection.ts. Do not edit here: edit there, then run scripts/sync-chat-core.cjs.
import { io, type Socket } from 'socket.io-client';
import { HubEvent, HubReady, HubRequestError, HubTransport } from './hub-types';

/**
 * The browser's connection to the messaging hub (tasks/messaging-hub-architecture.md §5, §9).
 *
 * Framework-free. The hub keeps no passwords: before EVERY connect and reconnect this asks the app's own API for a
 * one-use, 60-second hub token (`tokenProvider`), because a socket token can be used once and a reconnect is a new
 * connection. The hub URL comes back with the token, so the app needs no hub configuration of its own.
 *
 * Requests are `emit(event, payload, ack)` with a timeout; a `{ ok: false, error }` reply becomes a HubRequestError
 * carrying the hub's code. Server events and every (re)connect's `hub:ready` go to listeners.
 */

export interface HubTokenGrant {
  hubUrl: string;
  token: string;
}

export type HubStatus = 'idle' | 'connecting' | 'ready' | 'reconnecting' | 'unavailable';

const REQUEST_TIMEOUT_MS = 15_000;
const RETRY_MAX_MS = 30_000;

export class HubConnection implements HubTransport {
  private socket: Socket | null = null;
  private readonly eventListeners = new Set<(e: HubEvent) => void>();
  private readonly readyListeners = new Set<(r: HubReady) => void>();
  private readonly statusListeners = new Set<(s: HubStatus, code?: string) => void>();
  private status: HubStatus = 'idle';
  private hubUrl: string | null = null;
  private retry: ReturnType<typeof setTimeout> | null = null;
  private retries = 0;
  private readonly retryDelayMs: number;

  constructor(
    private readonly tokenProvider: () => Promise<HubTokenGrant>,
    options: { retryDelayMs?: number } = {},
  ) {
    this.retryDelayMs = options.retryDelayMs ?? 1000;
  }

  get currentStatus(): HubStatus {
    return this.status;
  }

  /** The hub's base URL, from the first token (files go over HTTP to the same hub). */
  baseUrl(): string | null {
    return this.hubUrl;
  }

  onStatus(listener: (s: HubStatus, code?: string) => void): () => void {
    this.statusListeners.add(listener);
    listener(this.status);
    return () => this.statusListeners.delete(listener);
  }

  onEvent(listener: (e: HubEvent) => void): () => void {
    this.eventListeners.add(listener);
    return () => this.eventListeners.delete(listener);
  }

  onReady(listener: (r: HubReady) => void): () => void {
    this.readyListeners.add(listener);
    return () => this.readyListeners.delete(listener);
  }

  private setStatus(s: HubStatus, code?: string): void {
    this.status = s;
    for (const l of this.statusListeners) l(s, code);
  }

  /** Connects (once). Resolves after the first token is fetched; `hub:ready` follows through onReady. */
  async start(): Promise<void> {
    if (this.socket) return;
    this.setStatus('connecting');
    let first: HubTokenGrant;
    try {
      first = await this.tokenProvider();
    } catch (e) {
      this.setStatus('unavailable', e instanceof Error ? e.message : 'TOKEN_UNAVAILABLE');
      throw e;
    }
    let spare: HubTokenGrant | null = first;
    this.hubUrl = first.hubUrl.replace(/\/+$/, '');
    const socket = io(first.hubUrl, {
      path: '/socket',
      transports: ['websocket'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 30_000,
      // A fresh one-use token for every attempt; the first attempt uses the one fetched above.
      auth: (cb: (data: object) => void) => {
        const grant = spare;
        spare = null;
        (grant ? Promise.resolve(grant) : this.tokenProvider()).then(
          (g) => cb({ token: g.token }),
          () => cb({ token: '' }),
        );
      },
    });
    this.socket = socket;
    socket.on('hub:ready', (ready: HubReady) => {
      this.retries = 0;
      this.setStatus('ready');
      for (const l of this.readyListeners) l(ready);
    });
    socket.on('disconnect', () => {
      this.setStatus('reconnecting');
      this.retryIfGivenUp(socket);
    });
    socket.on('connect_error', (err: Error) => {
      this.setStatus('reconnecting', err.message);
      this.retryIfGivenUp(socket);
    });
    socket.onAny((event: string, data: unknown) => {
      if (event === 'hub:ready') return;
      for (const l of this.eventListeners) l({ event, data });
    });
  }

  /**
   * socket.io stops retrying for good when the hub turns a connection away (its sign-in check failed, e.g. while it is
   * still starting) or drops one it accepted. Those are usually passing, so keep trying, waiting longer each time.
   */
  private retryIfGivenUp(socket: Socket): void {
    if (socket.active || this.retry || this.socket !== socket) return;
    const delay = Math.min(this.retryDelayMs * 2 ** this.retries, RETRY_MAX_MS);
    this.retries += 1;
    this.retry = setTimeout(() => {
      this.retry = null;
      if (this.socket === socket && !socket.active) socket.connect();
    }, delay);
  }

  stop(): void {
    if (this.retry) clearTimeout(this.retry);
    this.retry = null;
    this.retries = 0;
    this.socket?.removeAllListeners();
    this.socket?.disconnect();
    this.socket = null;
    this.setStatus('idle');
  }

  request<T>(event: string, payload: unknown = {}): Promise<T> {
    const socket = this.socket;
    if (!socket || !socket.connected) return Promise.reject(new HubRequestError('NOT_CONNECTED'));
    return new Promise<T>((resolve, reject) => {
      socket.timeout(REQUEST_TIMEOUT_MS).emit(event, payload, (err: Error | null, reply?: { ok: boolean; data?: T; error?: string }) => {
        if (err) reject(new HubRequestError('TIMEOUT'));
        else if (!reply?.ok) reject(new HubRequestError(reply?.error ?? 'INTERNAL'));
        else resolve(reply.data as T);
      });
    });
  }
}
